<template>
  <div>
    <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>
    <h5 class="title is-5">{{ $t("profile.edit") }}</h5>
    <div class="content-box">
      <vue-element-loading :active="isLoading" spinner="spinner" />
      <Form
        @submit="onSubmit"
        :validation-schema="schema"
        :initial-values="formValues"
        v-slot="{ errors }"
      >
        <div class="field">
          <label class="label">{{ $t("Name") }} *</label>
          <div class="control">
            <Field
              name="userName"
              class="input"
              type="text"
              placeholder="ex) Your Name"
              maxlength="60"
              required="required"
              :class="{ 'is-danger': errors.userName }"
            />
            <span class="help is-danger">{{ errors.userName }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("profile.lang") }} *</label>
          <div class="select">
            <Field name="lang" as="select" class="form-control select-lang">
              <option value="ja">{{ $t("ja") }}</option>
              <option value="en-US">{{ $t("en-US") }}</option>
            </Field>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("profile.alert1") }} *</label>
          <div class="select">
            <Field name="alert1" as="select" class="form-control select-alert">
              <option value="t">{{
                $t("profile.alert1t") + " - " + $t("profile.alert1tMessage")
              }}</option>
              <option value="f">{{
                $t("profile.alert1f") + " - " + $t("profile.alert1fMessage")
              }}</option>
            </Field>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("profile.alert2") }} *</label>
          <div class="select">
            <Field
              name="alert2_combined"
              as="select"
              class="form-control select-alert"
            >
              <option value="t_f">{{
                $t("profile.alert2t") + " - " + $t("profile.alert2DailyMessage")
              }}</option>
              <option value="t_t">{{
                $t("profile.alert2t") +
                  " - " +
                  $t("profile.alert2OnlyWithEventsMessage")
              }}</option>
              <option value="f_f">{{
                $t("profile.alert2f") + " - " + $t("profile.alert2Message")
              }}</option>
            </Field>
          </div>
        </div>

        <router-link
          :to="{
            name: 'profileView'
          }"
          class="button"
          :data-tooltip="$t('Back')"
        >
          Cancel
        </router-link>

        <button
          type="submit"
          class="button is-primary ml-3"
          v-bind:class="{ 'is-loading': isLoad }"
        >
          {{ $t("Confirm") }}
        </button>
      </Form>
    </div>
  </div>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import imsr from "@/imsr-api";
import { ref } from "@vue/reactivity";
import { Field, Form } from "vee-validate";
import VueElementLoading from "vue-element-loading";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import * as Yup from "yup";

export default {
  components: {
    Breadcrumb,
    VueElementLoading,
    Form,
    Field
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const isLoad = ref(false);
    const formValues = ref({});
    const breadcrumb = ref([
      {
        title: "Home",
        path: {
          name: "dashboard",
          params: { projectId: store.getters.projectId }
        }
      },
      {
        title: "Profile",
        path: {
          name: "profileView"
        }
      },
      { title: "Edit", path: "#", isActive: true }
    ]);
    const isLoading = ref(false);

    const getUser = async () => {
      try {
        isLoading.value = true;
        const params = {
          tenantId: store.getters.tenantId
        };
        const userData = await imsr.imsrGetApi("/users", params);
        // alert2とalert2_only_with_eventsの組み合わせを統合値に変換
        const alert2 = userData.alert2 || "t";
        const alert2OnlyWithEvents = userData.alert2_only_with_events || "f";
        userData.alert2_combined = `${alert2}_${alert2OnlyWithEvents}`;
        formValues.value = userData;
        isLoading.value = false;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    getUser();

    const schema = Yup.object().shape({
      userName: Yup.string().required(t("validate.required")),
      lang: Yup.string().required(t("validate.required")),
      alert1: Yup.string().required(t("validate.required")),
      alert2_combined: Yup.string().required(t("validate.required"))
    });

    const onSubmit = async values => {
      try {
        isLoad.value = true;
        // alert2_combinedから個別の値に分割
        const [alert2, alert2OnlyWithEvents] = values.alert2_combined.split(
          "_"
        );
        const params = {
          tenantId: store.getters.tenantId,
          userName: values.userName,
          lang: values.lang,
          alert1: values.alert1,
          alert2: alert2,
          alert2_only_with_events: alert2OnlyWithEvents
        };
        await imsr.imsrPutApi("/users", params);
        isLoad.value = false;
        router.push({ name: "profileView" });
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    return {
      schema,
      onSubmit,
      isLoading,
      isLoad,
      formValues,
      breadcrumb
    };
  }
};
</script>
